import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, LifeBuoy, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  open: "border-[#B33A1F] text-[#B33A1F]",
  answered: "border-[#C9A961] text-[#8C6F1E]",
  closed: "border-neutral-300 text-neutral-500",
};

export default function HelpdeskPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", category: "general" });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/tickets"); setRows(data); }
    catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket raised"); setOpen(false); setForm({ subject: "", body: "", category: "general" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed")); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/tickets/${selected.id}/reply`, { body: reply });
      setSelected(data); setReply(""); load();
    } catch (e) { toast.error("Failed"); }
  };

  const close = async () => {
    try { await api.patch(`/tickets/${selected.id}`, { status: "closed" }); toast.success("Closed"); setSelected(null); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <PageHeader eyebrow="Support" title="Helpdesk." testid="page-helpdesk"
        description={user.role === "employee" ? "Raise a ticket for anything — payslip, leave, IT, general." : "Answer employee questions. Close when resolved."}
        actions={user.role === "employee" && (
          <button onClick={() => setOpen(true)} data-testid="btn-new-ticket"
            className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
            <Plus className="w-4 h-4" /> Raise a ticket
          </button>
        )}
      />
      <div className="p-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-2" data-testid="tickets-list">
          {loading && <div className="font-mono text-xs text-neutral-500">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="border border-dashed border-[#C9A961] p-10 text-center font-mono text-sm text-neutral-500">
              <LifeBuoy className="w-8 h-8 mx-auto mb-3 text-[#8C6F1E]" /> No tickets yet.
            </div>
          )}
          {rows.map((t) => (
            <button key={t.id} onClick={() => setSelected(t)} data-testid={`ticket-${t.id}`}
              className={`w-full text-left border p-4 bg-white transition-colors ${selected?.id === t.id ? "border-[#C9A961]" : "border-neutral-200 hover:border-[#C9A961]"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{t.category}</span>
                <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${STATUS_COLOR[t.status]}`}>{t.status}</span>
              </div>
              <div className="font-serif font-bold text-lg tracking-tight">{t.subject}</div>
              <div className="text-xs text-neutral-500 mt-1 font-mono">{t.employee_name} · {t.created_at?.slice(0, 10)} · {t.replies?.length || 0} replies</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-7">
          {selected ? (
            <div className="border border-neutral-200 bg-white" data-testid="ticket-detail">
              <div className="border-b border-neutral-200 px-6 py-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">{selected.category} · {selected.employee_name}</div>
                  <div className="font-serif font-bold text-2xl tracking-tight mt-1">{selected.subject}</div>
                </div>
                <button onClick={() => setSelected(null)}><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                <div className="bg-[#FAF7EF] p-4 border border-neutral-200">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">{selected.employee_name} · original</div>
                  <p className="text-sm whitespace-pre-wrap">{selected.body}</p>
                </div>
                {(selected.replies || []).map((r) => (
                  <div key={r.id} className={`p-4 border ${r.role === "company_admin" ? "border-[#C9A961] bg-[#FAF7EF]" : "border-neutral-200"}`}>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E] mb-1">{r.by} · {r.role} · {r.at?.slice(0, 16).replace("T", " ")}</div>
                    <p className="text-sm whitespace-pre-wrap">{r.body}</p>
                  </div>
                ))}
              </div>
              {selected.status !== "closed" && (
                <div className="border-t border-neutral-200 p-4 space-y-2">
                  <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="ticket-reply" placeholder="Type a reply…"
                    className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
                  <div className="flex justify-between">
                    {user.role === "company_admin" && <button onClick={close} data-testid="ticket-close" className="text-xs font-mono text-neutral-500 hover:text-[#B33A1F]">Close ticket</button>}
                    <button onClick={sendReply} data-testid="ticket-send-reply" className="ml-auto px-4 py-2 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">Send reply</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-neutral-300 p-16 text-center font-mono text-sm text-neutral-500">Select a ticket to view the thread.</div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-neutral-950/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Support</div><div className="font-serif font-bold text-2xl tracking-tight">Raise a ticket</div></div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Category</label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="ticket-category" className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm bg-white">
                  {["payroll","attendance","leave","it","general"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <input required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} data-testid="ticket-subject" placeholder="Subject"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-serif text-lg" />
              <textarea required rows={5} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="ticket-body" placeholder="Describe the issue…"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              <button type="submit" data-testid="btn-submit-ticket" className="w-full px-5 py-3 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">Raise ticket</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
